import styled from 'styled-components';

export const SettingsContainer = styled.div`
   max-width: 1200px;
   margin: 2rem auto;
   padding: 0 2rem;
`;

export const SettingsHeader = styled.div`
   margin-bottom: 2rem;

   h1 {
      font-size: 2.5rem;
      color: var(--primary-color);
      margin-bottom: 0.5rem;
   }

   p {
      color: var(--text-secondary);
      font-size: 1.1rem;
   }
`;

export const TabContainer = styled.div`
   display: flex;
   gap: 1rem;
   border-bottom: 2px solid var(--border-color);
   margin-bottom: 2rem;
   overflow-x: auto;

   @media (max-width: 768px) {
      gap: 0.5rem;
   }
`;

export const Tab = styled.button`
   padding: 1rem 1.5rem;
   font-size: 1rem;
   font-weight: 500;
   background: none;
   border: none;
   border-bottom: 3px solid transparent;
   color: ${props => (props.$active ? 'var(--primary-color)' : 'var(--text-secondary)')};
   border-bottom-color: ${props => (props.$active ? 'var(--primary-color)' : 'transparent')};
   cursor: pointer;
   transition: all 0.3s ease;
   white-space: nowrap;

   &:hover {
      color: var(--primary-color);
      background-color: rgba(0, 123, 255, 0.05);
   }

   @media (max-width: 768px) {
      padding: 0.75rem 1rem;
      font-size: 0.9rem;
   }
`;

export const TabContent = styled.div`
   background: white;
   border-radius: 8px;
   padding: 2rem;
   box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

   @media (max-width: 768px) {
      padding: 1.5rem;
   }
`;

export const Section = styled.div`
   margin-bottom: 2.5rem;

   &:last-child {
      margin-bottom: 0;
   }
`;

export const SectionTitle = styled.h2`
   font-size: 1.5rem;
   color: var(--text-primary);
   margin-bottom: 1rem;
   padding-bottom: 0.5rem;
   border-bottom: 1px solid var(--border-color);
`;

export const InfoGrid = styled.div`
   display: grid;
   gap: 1.5rem;
   grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
`;

export const InfoItem = styled.div`
   label {
      display: block;
      font-weight: 600;
      color: var(--text-secondary);
      font-size: 0.9rem;
      margin-bottom: 0.5rem;
      text-transform: uppercase;
      letter-spacing: 0.5px;
   }

   p {
      font-size: 1rem;
      color: var(--text-primary);
      margin: 0;
   }
`;

export const FormGroup = styled.div`
   margin-bottom: 1.5rem;

   label {
      display: block;
      font-weight: 600;
      color: var(--text-primary);
      margin-bottom: 0.5rem;
   }

   input {
      width: 100%;
      padding: 0.75rem;
      border: 1px solid var(--border-color);
      border-radius: 4px;
      font-size: 1rem;
      transition: border-color 0.3s ease;

      &:focus {
         outline: none;
         border-color: var(--primary-color);
      }

      &:disabled {
         background-color: #f5f5f5;
         cursor: not-allowed;
      }
   }

   small {
      display: block;
      margin-top: 0.5rem;
      color: var(--text-secondary);
      font-size: 0.875rem;
   }
`;

export const Button = styled.button`
   padding: 0.75rem 1.5rem;
   font-size: 1rem;
   font-weight: 500;
   border: none;
   border-radius: 4px;
   cursor: pointer;
   transition: all 0.3s ease;
   background-color: ${props =>
      props.$variant === 'danger'
         ? '#dc3545'
         : props.$variant === 'secondary'
         ? '#6c757d'
         : 'var(--primary-color)'};
   color: white;

   &:hover {
      opacity: 0.9;
      transform: translateY(-2px);
   }

   &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      transform: none;
   }

   &:not(:last-child) {
      margin-right: 1rem;
   }
`;

export const Card = styled.div`
   background: #f8f9fa;
   border: 1px solid var(--border-color);
   border-radius: 8px;
   padding: 1.5rem;
   margin-bottom: 1rem;
`;

export const CardHeader = styled.div`
   display: flex;
   justify-content: space-between;
   align-items: center;
   margin-bottom: 1rem;

   h3 {
      font-size: 1.1rem;
      color: var(--text-primary);
      margin: 0;
   }
`;

export const Badge = styled.span`
   padding: 0.25rem 0.75rem;
   border-radius: 12px;
   font-size: 0.875rem;
   font-weight: 500;
   background-color: ${props =>
      props.$status === 'active'
         ? '#28a745'
         : props.$status === 'pending'
         ? '#ffc107'
         : '#6c757d'};
   color: white;
`;

export const List = styled.ul`
   list-style: none;
   padding: 0;
   margin: 0;
`;

export const ListItem = styled.li`
   display: flex;
   justify-content: space-between;
   align-items: center;
   padding: 1rem;
   border: 1px solid var(--border-color);
   border-radius: 4px;
   margin-bottom: 0.5rem;
   background: white;

   &:last-child {
      margin-bottom: 0;
   }
`;

export const EmptyState = styled.div`
   text-align: center;
   padding: 3rem 2rem;
   color: var(--text-secondary);

   svg {
      font-size: 4rem;
      margin-bottom: 1rem;
      opacity: 0.5;
   }

   p {
      font-size: 1.1rem;
      margin: 0;
   }
`;

export const Alert = styled.div`
   padding: 1rem;
   border-radius: 4px;
   margin-bottom: 1.5rem;
   background-color: ${props =>
      props.$type === 'success'
         ? '#d4edda'
         : props.$type === 'warning'
         ? '#fff3cd'
         : props.$type === 'error'
         ? '#f8d7da'
         : '#d1ecf1'};
   border: 1px solid
      ${props =>
         props.$type === 'success'
            ? '#c3e6cb'
            : props.$type === 'warning'
            ? '#ffeeba'
            : props.$type === 'error'
            ? '#f5c6cb'
            : '#bee5eb'};
   color: ${props =>
      props.$type === 'success'
         ? '#155724'
         : props.$type === 'warning'
         ? '#856404'
         : props.$type === 'error'
         ? '#721c24'
         : '#0c5460'};
`;
